import { apiClient } from '@indipia/shared';

// ── 아이디 중복 체크 ──────────────────────────────────────────────────────

/** 아이디 사용 가능 여부 — GET /member/check-username?loginId= */
export async function checkLoginId(loginId: string): Promise<boolean> {
  const res = await apiClient.get('/member/check-username', { params: { loginId } });
  return (res.data.data as { available: boolean }).available;
}

// ── 일반 회원가입 ────────────────────────────────────────────────────────

export interface NormalJoinRequest {
  loginId:            string;
  email:              string;
  password:           string;
  memberName:         string;
  mobile:             string;
  /** 휴대폰 인증 완료 토큰 — 같은 번호의 퇴사·거절 직원 계정을 닫고 가입할 때 서버가 확인한다 */
  verifyToken?:       string;
  master:             boolean;
  compNo:              string;
  compType?:           string;
  compName?:          string;
  compRepName?:        string;
  compPhone?:         string;
  compEmail?:         string;
  compAddress?:       string;
  compAddressDtl?:    string;
  compGpsLat?:        number;
  compGpsLng?:        number;
  marketingConsent:   boolean;
  marketingChannels:  string[];
  nightMarketingConsent: boolean;
  /** 추천 링크로 들어왔으면 그 코드(마스터만, 선택, 2026-09-22 신설) — C-8 추천인 초대,
   *  ReferralLandingPage.tsx가 localStorage에 저장해둔 값을 여기 실어보낸다 */
  referralCode?:      string;
}

/** 일반(아이디/비밀번호) 회원가입 — POST /member/join */
export async function joinNormal(req: NormalJoinRequest): Promise<JoinResult> {
  const res = await apiClient.post('/member/join', {
    ...req,
    joinChannel: 'WEB',
  });
  return res.data.data as JoinResult;
}

// ── SNS 회원가입 ─────────────────────────────────────────────────────────

export interface SnsJoinRequest {
  snsType:            string;
  snsSubjectId:       string;
  memberName:         string;
  email:              string;
  mobile:             string;
  master:             boolean;
  compNo:              string;
  compType?:           string;
  compName?:          string;
  compRepName?:        string;
  compPhone?:         string;
  compEmail?:         string;
  compAddress?:       string;
  compAddressDtl?:    string;
  compGpsLat?:        number;
  compGpsLng?:        number;
  marketingConsent:   boolean;
  marketingChannels:  string[];
  nightMarketingConsent: boolean;
  /** 추천 링크로 들어왔으면 그 코드(마스터만, 선택, 2026-09-22 신설) — NormalJoinRequest.referralCode와 동일 용도 */
  referralCode?:      string;
}

export interface JoinResult {
  memberId:   number;
  loginId:    string;
  memberName: string;
  masterYn:   string;
  compId:     number;
  compName:   string;
  studioId:   number | null;
}

/** SNS 회원가입 (마스터/직원 공용) — POST /member/join/sns */
export async function joinSns(req: SnsJoinRequest): Promise<JoinResult> {
  const res = await apiClient.post('/member/join/sns', {
    ...req,
    joinChannel: 'WEB',
  });
  return res.data.data as JoinResult;
}

// ── 사업자 조회 ──────────────────────────────────────────────────────────

export type BizStatus = 'VALID_NEW' | 'VALID_EXISTING' | 'CLOSED';

export interface BizVerifyResult {
  bizStatus:     BizStatus;
  compId:        number | null;
  companyName:   string | null;
  compRepName:    string | null;
  hasMaster:     boolean | null;
  noticeMessage: string | null;
}

/** 마스터용 국세청 사업자 조회 — POST /member/verify-business-number */
export async function verifyBusinessNumber(businessNumber: string): Promise<BizVerifyResult> {
  const res = await apiClient.post('/member/verify-business-number', { businessNumber });
  return res.data.data as BizVerifyResult;
}

export interface BizInfo {
  compId:     number;
  compNo:      string;
  compName:   string;
  compRepName: string;
}

/** 직원용 소속 사업자 조회 — GET /member/biz/info?compNo= */
export async function getBizInfo(compNo: string): Promise<BizInfo> {
  const res = await apiClient.get('/member/biz/info', { params: { compNo } });
  return res.data.data as BizInfo;
}

export interface BizSearchItem {
  compId:     number;
  compNo:      string;
  compName:   string;
  compRepName: string;
  hasMaster:  boolean;
}

/** 상호명 키워드 검색 — GET /member/biz/search?keyword=&hasMaster=Y */
export async function searchBiz(keyword: string): Promise<BizSearchItem[]> {
  const res = await apiClient.get('/member/biz/search', { params: { keyword, hasMaster: 'Y' } });
  return res.data.data as BizSearchItem[];
}
